import React from 'react';
import { Link } from 'react-router-dom';

import { AnimatedCounter } from '../components/animated-counter';
import { ComplianceFlow } from '../components/compliance-flow';
import { ContactForm } from '../components/contact-form';
import { IndustryTabs } from '../components/industry-tabs';
import { PartnerCarousel } from '../components/partner-carousel';
import { ScrollReveal } from '../components/scroll-reveal';

export default function Home() {
  return (
    <div>
      <section className="relative overflow-hidden hero-gradient">
        <div className="pointer-events-none absolute inset-0 grid-overlay" />
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border-subtle bg-white/5 px-4 py-2 text-sm text-text-secondary">
                <span className="h-2 w-2 rounded-full bg-[color:var(--accent-compliance)] shadow-glowCompliance" />
                Continuous, evidence-based compliance engineering
              </div>

              <h1 className="mt-6 text-balance text-4xl font-extrabold leading-[1.03] tracking-[-0.03em] text-text-primary md:text-6xl">
                The Compliance
                <span
                  className="text-gradient"
                  style={{ animation: "gradient-shift 12s ease infinite" }}
                >
                  {" "}
                  Command Center
                </span>
                .
                <br />
                Engineering Trust.
                <br />
                Automating Evidence.
              </h1>

              <p className="mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-text-secondary md:text-xl">
                India&apos;s premier RegTech Centre of Excellence—enabling BFSI,
                Government, and Critical Infrastructure to achieve continuous,
                audit-ready compliance.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/assessment"
                  className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white shadow-[0_0_60px_-22px_rgba(16,185,129,0.65)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  Run Compliance Scan
                </Link>
                <Link
                  to="/technology-mapping"
                  className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
                >
                  Build My Stack
                </Link>
                <Link
                  to="/frameworks/india/dpdpa"
                  className="focus-ring inline-flex h-12 items-center justify-center rounded-xl px-5 text-sm font-semibold text-[color:var(--link)] transition-colors hover:text-text-primary"
                >
                  Open DPDPA ↓
                </Link>
              </div>

              <div className="mt-12 grid gap-4 border-t border-border-subtle pt-8 sm:grid-cols-3">
                <div className="glass-card sheen p-5">
                  <div className="font-[family:var(--font-space-grotesk)] text-3xl font-semibold tracking-[-0.02em] text-text-primary">
                    50+
                  </div>
                  <div className="mt-1 text-sm text-text-secondary">
                    Enterprises enabled
                  </div>
                </div>
                <div className="glass-card sheen p-5">
                  <div className="font-[family:var(--font-space-grotesk)] text-3xl font-semibold tracking-[-0.02em] text-text-primary">
                    10+
                  </div>
                  <div className="mt-1 text-sm text-text-secondary">
                    Framework routes live
                  </div>
                </div>
                <div className="glass-card sheen p-5">
                  <div className="font-[family:var(--font-space-grotesk)] text-3xl font-semibold tracking-[-0.02em] text-text-primary">
                    Evidence
                  </div>
                  <div className="mt-1 text-sm text-text-secondary">
                    First. Always.
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="glass-card sheen p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Live posture preview
                </div>
                <div className="mt-3 text-lg font-semibold text-text-primary">
                  Unified Compliance Dashboard
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Overall compliance
                    </div>
                    <div className="mt-2 font-[family:var(--font-space-grotesk)] text-4xl font-semibold text-text-primary">
                      82%
                    </div>
                    <div className="mt-2 text-sm text-text-secondary">↑ 8% vs Q3</div>
                  </div>

                  <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Penalty exposure
                    </div>
                    <div className="mt-2 font-[family:var(--font-space-grotesk)] text-4xl font-semibold text-text-primary">
                      ₹250Cr
                    </div>
                    <div className="mt-2 text-sm text-text-secondary">Top tier risks tracked</div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Domain readiness
                  </div>
                  <div className="mt-3 space-y-2">
                    {[
                      { k: "DPDPA — Consent & Notice", v: 85 },
                      { k: "DPDPA — Rights (DSAR)", v: 72 },
                      { k: "RBI — SOC & Monitoring", v: 90 },
                      { k: "Cross-border mapping", v: 60 },
                    ].map((x) => (
                      <div key={x.k} className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-text-secondary">
                          <span>{x.k}</span>
                          <span>{x.v}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-white/5">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#3B82F6]"
                            style={{ width: `${x.v}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <Link
                    to="/dashboards"
                    className="focus-ring inline-flex h-11 flex-1 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
                  >
                    View dashboards
                  </Link>
                  <Link
                    to="/frameworks/india/dpdpa"
                    className="focus-ring inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] px-4 text-sm font-semibold text-white hover:opacity-95"
                  >
                    DPDPA controls
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="explore">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="max-w-3xl">
              <h2 className="text-balance text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                Explore the RegTech CoE
              </h2>
              <p className="mt-4 max-w-2xl text-lg text-text-secondary">
                Jump into frameworks, technology mapping, OT/ICS security, dashboards, and delivery offerings.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "DPDPA (Privacy)", d: "85+ controls, penalties, tech stack", href: "/frameworks/india/dpdpa" },
              { t: "RBI Compliance", d: "Control catalog + mapping", href: "/frameworks/india/rbi" },
              { t: "CERT-In", d: "Incident reporting controls", href: "/frameworks/india/cert-in" },
              { t: "SEBI CSCRF", d: "Cybersecurity resilience", href: "/frameworks/india/sebi-cscrf" },
              { t: "IRDAI", d: "Insurance security posture", href: "/frameworks/india/irdai" },
              { t: "ISO 27001", d: "ISMS controls + mappings", href: "/frameworks/international/iso-27001" },
              { t: "PCI-DSS", d: "Card data security standard", href: "/frameworks/international/pci-dss" },
              { t: "NIST CSF", d: "Functions + subcategories", href: "/frameworks/international/nist-csf" },
              { t: "DORA", d: "EU operational resilience", href: "/frameworks/international/dora" },
              { t: "OT/ICS Security", d: "Purdue model + SOC integration", href: "/ot-security" },
              { t: "Technology Mapping", d: "Controls → tools → evidence", href: "/technology-mapping" },
              { t: "Dashboards", d: "Board + CISO views", href: "/dashboards" },
              { t: "Offerings", d: "Assessment → automation", href: "/offerings" },
              { t: "Resources", d: "Catalog library", href: "/resources" },
              { t: "Contact", d: "Workshops + consult", href: "/contact" },
              { t: "Assessment", d: "Start your scan", href: "/assessment" },
            ].map((c) => (
              <ScrollReveal key={c.href}>
                <Link
                  to={c.href}
                  className="glass-card sheen block p-6 transition-transform hover:scale-[1.01]"
                >
                  <div className="text-lg font-semibold text-text-primary">{c.t}</div>
                  <div className="mt-2 text-sm text-text-secondary">{c.d}</div>
                  <div className="mt-4 text-sm font-semibold text-[color:var(--text-link)]">
                    Open →
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="max-w-3xl">
              <h2 className="text-balance text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                Compliance isn't a checkbox.
                <br />
                It's continuous engineering.
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-text-secondary">
                Multiple overlapping frameworks, manual evidence work, and tool
                sprawl make compliance reactive. We engineer it into continuous,
                testable systems.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-12 grid gap-4 md:grid-cols-2">
            <ScrollReveal delayMs={0}>
              <div className="glass-card sheen p-6">
                <div className="text-sm font-semibold text-text-primary">
                  Regulatory complexity
                </div>
                <div className="mt-2 text-sm text-text-secondary">
                  Constant updates and interpretations across RBI, CERT-In,
                  SEBI, IRDAI, and global standards.
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delayMs={80}>
              <div className="glass-card sheen p-6">
                <div className="text-sm font-semibold text-text-primary">
                  Manual evidence
                </div>
                <div className="mt-2 text-sm text-text-secondary">
                  Screenshots, emails, and spreadsheets—audit prep takes months,
                  not minutes.
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delayMs={140}>
              <div className="glass-card sheen p-6">
                <div className="text-sm font-semibold text-text-primary">
                  Tool sprawl
                </div>
                <div className="mt-2 text-sm text-text-secondary">
                  Dozens of tools with no clear regulatory linkage or evidence
                  flow.
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delayMs={220}>
              <div className="glass-card sheen p-6">
                <div className="text-sm font-semibold text-text-primary">
                  Point-in-time assurance
                </div>
                <div className="mt-2 text-sm text-text-secondary">
                  Audit-ready today, non-compliant tomorrow—no continuous posture
                  visibility.
                </div>
              </div>
            </ScrollReveal>
          </div>

          <div className="mt-12 glass-card p-6">
            <div className="grid gap-4 md:grid-cols-3 md:items-center">
              <div className="text-sm font-semibold text-text-secondary">
                Manual, Reactive, Siloed
              </div>
              <div className="hidden h-[1px] w-full bg-white/10 md:block" />
              <div className="text-sm font-semibold text-text-primary md:text-right">
                Automated, Continuous, Unified
              </div>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-border-subtle bg-white/5 p-4">
                <div className="font-[family:var(--font-space-grotesk)] text-2xl font-semibold text-text-primary">
                  85%
                </div>
                <div className="mt-1 text-sm text-text-secondary">
                  reduction in audit preparation time
                </div>
              </div>
              <div className="rounded-2xl border border-border-subtle bg-white/5 p-4">
                <div className="font-[family:var(--font-space-grotesk)] text-2xl font-semibold text-text-primary">
                  60%
                </div>
                <div className="mt-1 text-sm text-text-secondary">
                  fewer recurring compliance gaps
                </div>
              </div>
              <div className="rounded-2xl border border-border-subtle bg-white/5 p-4">
                <div className="font-[family:var(--font-space-grotesk)] text-2xl font-semibold text-text-primary">
                  Real-time
                </div>
                <div className="mt-1 text-sm text-text-secondary">
                  posture visibility for leadership
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                  Navigate every framework
                </h2>
                <p className="mt-3 text-lg text-text-secondary">
                  Implementation-ready control catalogs with automation potential
                  and evidence requirements.
                </p>
              </div>
              <Link
                to="/frameworks"
                className="focus-ring inline-flex h-11 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
              >
                View all frameworks →
              </Link>
            </div>
          </ScrollReveal>

          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "RBI Master Directions",
                desc: "IT Governance & Cyber Security",
                meta: "Domains: 15 • Controls: 127 • 78% Automatable",
                href: "/frameworks/india/rbi",
              },
              {
                title: "CERT-In Directions",
                desc: "Incident reporting & log retention",
                meta: "Incident Types: 21 • Logs: 180 days • 85% Automatable",
                href: "/frameworks/india/cert-in",
              },
              {
                title: "SEBI CSCRF",
                desc: "Cyber Security & Resilience",
                meta: "Pillars: 11 • Controls: 100+ • 72% Automatable",
                href: "/frameworks/india/sebi-cscrf",
              },
              {
                title: "IRDAI Guidelines",
                desc: "Insurance cybersecurity",
                meta: "Domains: 10 • Controls: 70+ • 75% Automatable",
                href: "/frameworks/india/irdai",
              },
              {
                title: "ISO 27001",
                desc: "Annex A controls",
                meta: "Controls: 93 • 80% Automatable",
                href: "/frameworks/international/iso-27001",
              },
              {
                title: "PCI-DSS",
                desc: "Payment card security",
                meta: "Reqs: 12 • Sub-controls: 250+ • 70% Automatable",
                href: "/frameworks/international/pci-dss",
              },
              {
                title: "NIST CSF",
                desc: "Functions & subcategories",
                meta: "Functions: 5 • Subcategories: 108 • 82% Automatable",
                href: "/frameworks/international/nist-csf",
              },
              {
                title: "DORA",
                desc: "Operational resilience",
                meta: "Pillars: 5 • 68% Automatable",
                href: "/frameworks/international/dora",
              },
            ].map((c) => (
              <ScrollReveal key={c.title} variant="scaleIn">
                <Link to={c.href} className="glass-card sheen block p-6">
                  <div className="text-sm font-semibold text-text-primary">
                    {c.title}
                  </div>
                  <div className="mt-2 text-sm text-text-secondary">{c.desc}</div>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {c.meta}
                  </div>
                  <div className="mt-5 text-sm font-semibold text-[color:var(--text-link)]">
                    View framework →
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              From regulation to evidence — engineered
            </h2>
          </ScrollReveal>
          <div className="mt-10">
            <ComplianceFlow />
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              Key capabilities
            </h2>
          </ScrollReveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Regulatory control engineering",
                body: "Decompose frameworks into domains and controls. Map intent to testable requirements.",
              },
              {
                title: "Continuous compliance automation",
                body: "Policy-as-code, automated testing, and continuous posture monitoring.",
              },
              {
                title: "Audit-ready evidence pipelines",
                body: "Automated evidence collection with chain-of-custody and pre-packaged audit bundles.",
              },
              {
                title: "Technology-to-regulation mapping",
                body: "Vendor-agnostic mappings that rationalize tools and justify investments.",
              },
              {
                title: "Board & regulator reporting",
                body: "Executive dashboards, RAG status, trends, and forecasting.",
              },
              {
                title: "IT + cloud + OT unified compliance",
                body: "Converged controls and visibility across environments.",
              },
            ].map((c) => (
              <ScrollReveal key={c.title}>
                <div className="glass-card sheen p-6">
                  <div className="text-sm font-semibold text-text-primary">
                    {c.title}
                  </div>
                  <div className="mt-2 text-sm text-text-secondary">{c.body}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              Industry verticals
            </h2>
          </ScrollReveal>
          <div className="mt-10">
            <IndustryTabs
              tabs={[
                {
                  key: "banking",
                  title: "Banking & Finance",
                  frameworks: "RBI Master Directions, PCI-DSS, ISO 27001",
                  focusAreas: [
                    "Digital channel security",
                    "Core banking protection",
                    "Third-party risk",
                    "Fraud prevention controls",
                    "24x7 SOC requirements",
                  ],
                  caseStudy:
                    "Large private bank achieves 95% RBI compliance in 12 weeks",
                  ctaHref: "/offerings",
                  ctaLabel: "Explore solutions",
                },
                {
                  key: "insurance",
                  title: "Insurance",
                  frameworks: "IRDAI, ISO 27001, NIST CSF",
                  focusAreas: [
                    "Policyholder data protection",
                    "Agent/TPA access control",
                    "Claims platform security",
                    "Fraud monitoring",
                    "Third-party assurance",
                  ],
                  caseStudy:
                    "Insurer reduces audit prep time by 80% using evidence pipelines",
                  ctaHref: "/offerings",
                  ctaLabel: "Explore solutions",
                },
                {
                  key: "markets",
                  title: "Capital Markets",
                  frameworks: "SEBI CSCRF, ISO 27001, NIST 800-53",
                  focusAreas: [
                    "Trading platform resilience",
                    "Identity & privileged access",
                    "SOC use cases",
                    "VAPT cadence",
                    "Regulatory reporting",
                  ],
                  caseStudy:
                    "Brokerage strengthens cyber resilience with mapped SEBI controls",
                  ctaHref: "/offerings",
                  ctaLabel: "Explore solutions",
                },
                {
                  key: "government",
                  title: "Government",
                  frameworks: "ISO 27001, NIST CSF, CERT-In",
                  focusAreas: [
                    "Data classification",
                    "Identity governance",
                    "Incident readiness",
                    "Secure SDLC",
                    "Vendor assurance",
                  ],
                  caseStudy:
                    "Agency modernizes compliance posture with continuous testing",
                  ctaHref: "/offerings",
                  ctaLabel: "Explore solutions",
                },
                {
                  key: "critical",
                  title: "Critical Infrastructure",
                  frameworks: "NIST 800-82, IEC 62443, NERC CIP",
                  focusAreas: [
                    "Purdue segmentation",
                    "Zone/conduit controls",
                    "Asset inventory",
                    "OT monitoring",
                    "Resilience testing",
                  ],
                  caseStudy:
                    "Utility aligns IT/OT SOC with unified evidence dashboards",
                  ctaHref: "/ot-security",
                  ctaLabel: "Explore OT security",
                },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              By the numbers
            </h2>
          </ScrollReveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { v: 500, s: "+", label: "Controls mapped" },
              { v: 12, s: "", label: "Frameworks covered" },
              { v: 50, s: "+", label: "Enterprise deployments" },
              { v: 85, s: "%", label: "Audit success rate" },
              { v: 78, s: "%", label: "Automation potential" },
              { v: 60, s: "%", label: "Gap reduction" },
            ].map((m) => (
              <div key={m.label} className="glass-card sheen p-6">
                <div className="font-[family:var(--font-space-grotesk)] text-4xl font-semibold tracking-[-0.03em] text-text-primary">
                  <AnimatedCounter value={m.v} suffix={m.s} />
                </div>
                <div className="mt-2 text-sm text-text-secondary">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-24 md:py-32">
        <div className="container-ivalue">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              Technology partners
            </h2>
          </ScrollReveal>
          <div className="mt-10">
            <PartnerCarousel
              categories={[
                {
                  title: "SIEM/SOAR",
                  partners: ["Splunk", "Chronicle", "Sentinel", "QRadar", "Elastic"],
                },
                {
                  title: "IAM/PAM",
                  partners: ["CyberArk", "SailPoint", "Okta", "Microsoft Entra"],
                },
                {
                  title: "GRC",
                  partners: ["ServiceNow", "Archer", "MetricStream"],
                },
                {
                  title: "Network",
                  partners: ["Palo Alto", "Fortinet", "Cisco"],
                },
                {
                  title: "Cloud Security",
                  partners: ["Wiz", "Prisma Cloud", "Lacework"],
                },
                {
                  title: "Endpoint",
                  partners: ["CrowdStrike", "SentinelOne", "Defender"],
                },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 md:py-32" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>
        <div className="container-ivalue relative">
          <ScrollReveal>
            <h2 className="text-balance text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
              Ready to engineer your compliance?
            </h2>
          </ScrollReveal>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="glass-card p-6">
              <div className="text-sm font-semibold text-text-primary">
                Contact iValue
              </div>
              <div className="mt-4">
                <ContactForm
                  title="Get a tailored compliance plan"
                  subtitle="Or open the full contact page for workshops + consulting."
                  ctaLabel="Send"
                />
              </div>

              <div className="mt-4">
                <Link
                  to="/contact#contact-form"
                  className="focus-ring inline-flex h-11 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
                >
                  Open full contact page →
                </Link>
              </div>
            </div>

            <div className="glass-card p-6">
              <div className="text-sm font-semibold text-text-primary">
                Downloads
              </div>
              <div className="mt-4 grid gap-3">
                {[
                  "RBI Control Catalog (PDF/Excel)",
                  "SEBI CSCRF Blueprint",
                  "Compliance Automation Guide",
                  "ROI Calculator",
                ].map((d) => (
                  <a
                    key={d}
                    href="#"
                    className="focus-ring flex items-center justify-between rounded-xl border border-border-subtle bg-white/5 px-4 py-3 text-sm font-semibold text-text-secondary transition-colors hover:bg-white/10 hover:text-text-primary"
                  >
                    <span>{d}</span>
                    <span className="text-[color:var(--text-link)]">↓</span>
                  </a>
                ))}
                <div className="text-xs text-text-tertiary">
                  Download links will be connected in Part 3.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
